import { router, usePathname } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { debugNav } from '@order/utils/getDebugLog'
import AppConfig from 'config'
import useIntroStore from 'hooks/useIntroStore'
import { useIsNavigationReady } from 'hooks/useIsNavigationReady'
import { useIsNotFoundRoute } from 'hooks/useIsNotFoundRoute'
import { useCustomer, useStaff } from 'hooks/useSession'

const devRoutes = ['/dev', '/admin']

const paths = {
  staff: ['/staff/panel', '/deals'],
}

export const replaceWithDismiss = (path: string) => {
  try {
    if (router.canDismiss()) {
      router.dismissAll()
    }
  } catch (e) {
    debugNav.warn('No routes to dismiss', e)
  }
  router.replace(path)
}

type RedirectTarget = {
  target: string
  allowed?: string[]
  denied?: string[]
}

const containsPath = (paths: string[], current: string) => {
  return paths.some((path) => current.startsWith(path))
}

const AuthRedirects = () => {
  const pathname = usePathname()
  const isNotFoundRoute = useIsNotFoundRoute()
  const customer = useCustomer()
  const staff = useStaff()
  const isNavigationReady = useIsNavigationReady()
  const redirect = useIntroStore((s) => s.redirect)
  const clearRedirect = useIntroStore((s) => s.clearRedirect)

  const path = useRef<string>(pathname)

  useEffect(() => {
    path.current = pathname
  }, [pathname])

  const redirectTo = useCallback(
    ({ target, allowed, denied }: RedirectTarget) => {
      // We are on a dev route and don't want to redirect
      if (
        AppConfig.isDev &&
        devRoutes.some((r) => path.current.startsWith(r))
      ) {
        return
      }

      // Skip redirect if current path is not allowed
      if (allowed && !containsPath(allowed, path.current)) {
        return
      }
      // Skip redirect if current path is denied
      if (denied && containsPath(denied, path.current)) {
        return
      }
      // Skip redirect if current path is the same as the target
      if (target === path.current) {
        return
      }

      debugNav.log('Nav ready, redirecting', {
        to: target,
      })

      replaceWithDismiss(target)
      path.current = target
      clearRedirect()
    },
    [clearRedirect]
  )

  // const [redirectTarget, setRedirectTarget] = useState<string | null>(null)

  const hasCustomer = !!customer
  const hasStaff = !!staff

  const redirectTarget = useMemo(() => {
    if (!isNavigationReady || isNotFoundRoute) {
      return undefined
    }
    if (AppConfig.isDev && AppConfig.dev.redirectsDisabled) {
      return undefined
    }

    if (!hasCustomer && !hasStaff) {
      return { target: '/' }
    }

    if (hasCustomer) {
      return { target: redirect || '/customer/home' }
    }

    if (hasStaff) {
      return {
        target: redirect || '/staff/panel',
        // Don't deny any paths if we have and explicit redirect
        denied: !redirect ? paths.staff : undefined,
      }
    }
    return undefined
  }, [hasCustomer, redirect, hasStaff, isNavigationReady, isNotFoundRoute])

  useEffect(() => {
    if (!redirectTarget) {
      return
    }
    redirectTo(redirectTarget)
  }, [redirectTarget, clearRedirect, redirectTo])

  return null
}

export default AuthRedirects
